import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  FocusableComponentLayout,
  FocusContext,
  useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import {
  isValidDistributorForInAppDonations,
  platformTransitionStyle,
} from '../../../../shared/helpers'
import fallbackLogo from '../../pbs-logo.svg'
import googleAnalytics4 from '../../lib/googleAnalytics'
import Spinner from '../../components/Spinner/Spinner'
import { useAppSelector } from '../../hooks/useStore'
import SettingsScreenModal from './SettinsScreenModal'
import { getPassportUrl } from './utils'
import useDonationStatus from './hooks/useDonationStatus'
import useGetClosedCaptionsData from './hooks/useGetClosedCaptionsData'
import useGetPrivacyPolicyData from './hooks/useGetPrivacyPolicyData'
import useGetTermsOfUseData from './hooks/useGetTermsOfUseData'
import SettingsAbout from './SettingsAbout'
import SettingsAccount from './SettingsAccount'
import SettingsChangeStation from './SettingsChangeStation'
import SettingsVideo from './SettingsVideo'
import { useSidePanel } from '../../components/SidePanel/SidePanelProvider'

const COLLECTIONS_SET_PADDING_TOP = 236

const SettingsScreen = () => {
  const {
    callSign,
    distributor,
    passportEligible,
    shortCommonName,
    stationHasDonationCampaign,
    stationLogo,
    upNextEnabled,
    user,
  } = useAppSelector((state) => ({
    callSign: state?.station.call_sign,
    distributor: state?.device?.distributor,
    passportEligible: state?.station.passportEligible,
    shortCommonName: state?.station.short_common_name,
    stationHasDonationCampaign:
      state?.station.donation_campaign.station_has_donation_campaign,
    stationLogo: state.station.white_cobranded_logo || fallbackLogo,
    upNextEnabled: state?.content.upNextEnabled,
    user: state?.user,
  }))
  const [translateY, setTranslateY] = useState(0)
  const [modal, setModal] = useState<'privacy' | 'terms' | 'captions' | null>(
    null
  )
  const refModalOption = useRef<string | null>(null)
  const { ref, focusKey } = useFocusable()
  const { keepSidePanelOpened } = useSidePanel()
  const donationFeatureEnabled =
    COMCAST_IN_APP_DONATIONS_ENABLED && COMPILING_FOR_COMCASTX1
  const canDonate = useMemo(
    () =>
      donationFeatureEnabled &&
      stationHasDonationCampaign &&
      isValidDistributorForInAppDonations(distributor),
    [donationFeatureEnabled, stationHasDonationCampaign, distributor]
  )
  const { isActiveDonation, unactivatedPassport } = useDonationStatus({
    callSign,
    enabled: donationFeatureEnabled,
  })
  const passportUrl = useMemo(
    () => getPassportUrl(canDonate, user, unactivatedPassport, callSign),
    [canDonate, user, unactivatedPassport, callSign]
  )
  const {
    title: privacyTitle,
    text: privacyText,
    loading: loadingPrivacy,
  } = useGetPrivacyPolicyData({ isVisible: modal === 'privacy' })
  const {
    title: termsTitle,
    text: termsText,
    loading: loadingTerms,
  } = useGetTermsOfUseData({ isVisible: modal === 'terms' })
  const {
    title: captionsTitle,
    text: captionsText,
    loading: loadingCaptions,
  } = useGetClosedCaptionsData({ isVisible: modal === 'captions' })
  const focusKeyMap = {
    privacy: 'privacyPolicyModal',
    terms: 'termsOfUseModal',
    captions: 'closedCaptionModal',
  }
  const modalTitle =
    modal === 'privacy'
      ? privacyTitle
      : modal === 'terms'
      ? termsTitle
      : captionsTitle
  const modalContent =
    modal === 'privacy'
      ? privacyText
      : modal === 'terms'
      ? termsText
      : captionsText

  if (modal) {
    refModalOption.current = focusKeyMap[modal]
  }

  useEffect(() => {
    googleAnalytics4.sendPageViewEvent('Settings')
  }, [])

  useEffect(() => {
    if (!modal) {
      keepSidePanelOpened(true)
    } else {
      keepSidePanelOpened(false)
    }
    return () => keepSidePanelOpened(false)
  }, [modal])

  const handleFocus = useCallback(
    (layout: FocusableComponentLayout) => {
      setTranslateY(-(layout.top - COLLECTIONS_SET_PADDING_TOP))
    },
    [setTranslateY]
  )

  const handleCloseModal = () => {
    googleAnalytics4.sendPageViewEvent('Settings')
    setModal(null)
  }

  return (
    <FocusContext.Provider value={focusKey}>
      <Spinner
        visible={loadingPrivacy || loadingTerms || loadingCaptions}
        screen
      />

      {modal && (
        <SettingsScreenModal
          title={modalTitle}
          content={modalContent}
          onClose={handleCloseModal}
          visible
        />
      )}
      {!modal && (
        <div
          style={{
            transform: `translateY(${translateY}px)`,
            transition: platformTransitionStyle('transform 400ms ease-in-out'),
          }}
        >
          <div className="settings-screen-container" ref={ref}>
            <SettingsChangeStation
              callSign={callSign}
              handleFocus={handleFocus}
              shortCommonName={shortCommonName}
              stationLogo={stationLogo}
              passportEligible={passportEligible}
              passportUrl={passportUrl}
              user={user}
            />
            <SettingsVideo
              upNextEnabled={upNextEnabled}
              handleFocus={handleFocus}
            />
            <SettingsAbout
              handleFocus={handleFocus}
              setClosedCaptionVisible={() => setModal('captions')}
              setPrivacyVisible={() => setModal('privacy')}
              setTermsOfVisible={() => setModal('terms')}
              returnFocusKey={refModalOption.current}
            />
            <SettingsAccount
              donationFeatureEnabled={donationFeatureEnabled}
              isActiveDonation={isActiveDonation}
              handleFocus={handleFocus}
              user={user}
            />
          </div>
        </div>
      )}
    </FocusContext.Provider>
  )
}

export default SettingsScreen
